/**
 * shared/collision/world.ts — MeshCollisionWorld: Q3-style AABB sweeps against brushes + triangles,
 * accelerated by a BVH. This is the real map collision (Hijacked) behind the CollisionWorld contract.
 *
 * OWNER: collision agent. Pure TS, deterministic: float64 + - * / sqrt only, fixed traversal order, so the
 * server (Node), the single-player Worker and client prediction (browser) get bit-identical results
 * (ARCHITECTURE §2 rule 4; checked by hijacked.test.ts + tests/e2e/collision-determinism.spec.ts).
 *
 * Semantics follow Q3 CM_TraceThroughBrush (cm_trace.c), the same core as BoxWorld.clipBoxToAabb:
 *  - every plane is expanded by the moving box: dist' = dist - n·(n_i < 0 ? maxs_i : mins_i);
 *  - the hit fraction is pulled back so the box stops SURFACE_CLIP_EPSILON (0.125) in front of the plane;
 *  - startsolid / allsolid as in Q3 (allsolid => fraction 0); the first plane with the largest enter
 *    fraction is reported (normal + *unexpanded* plane dist, i.e. Q3 `trace.plane = *clipplane`);
 *  - only primitives with (contents & contentMask) != 0 take part (WATER 0x20 is not in MASK_PLAYERSOLID,
 *    MISSILECLIP 0x80 only in MASK_MISSILESHOT, PLAYERCLIP 0x10000 only in player masks — types.ts).
 *  - entityNum = ENTITYNUM_WORLD on a hit / startsolid, else ENTITYNUM_NONE (Q3 SV_Trace).
 */
import { ENTITYNUM_NONE, ENTITYNUM_WORLD } from '../constants';
import type { ReadonlyVec3, Vec3 } from '../math';
import { buildBvh, type Bvh } from './bvh';
import type { CollisionGeometryBuilder } from './geometry';
import {
  CONTENTS, SURFACE_CLIP_EPSILON, surfaceTypeFromFlags, type CollisionWorld, type TraceResult,
} from './types';

const EPS = SURFACE_CLIP_EPSILON;
/**
 * Node cull margin (units). Must be >= EPS: a primitive can only report a hit (or startsolid) where the
 * swept box comes within EPS of every one of its axial planes, i.e. inside its AABB grown by EPS
 * (all primitives carry their 6 axial planes). 0.25 leaves headroom for rounding.
 */
const NODE_MARGIN = 0.25;
const STACK_SIZE = 256;
const ZERO3: ReadonlyVec3 = [0, 0, 0];

/** A water surface (triangle with CONTENTS.WATER): the column below it, down to the first solid, is water. */
interface WaterSurface { tri: number; mnx: number; mny: number; mxx: number; mxy: number }

export interface MeshWorldStats {
  brushes: number;
  brushPlanes: number;
  triangles: number;
  nodes: number;
  depth: number;
  waterSurfaces: number;
  bevelPlanes: number;
  axialPlanesAdded: number;
  rejectedBrushes: number;
  rejectedTris: number;
}

// ---- per-trace scratch (module scope: one trace at a time per JS realm) -----------------------------
let S0 = 0, S1 = 0, S2 = 0, E0 = 0, E1 = 0, E2 = 0;
let MN0 = 0, MN1 = 0, MN2 = 0, MX0 = 0, MX1 = 0, MX2 = 0;
// per-primitive clip state
let enterFrac = -1, leaveFrac = 1, startout = false, getout = false;
let hitNx = 0, hitNy = 0, hitNz = 0, hitD = 0, hitSurf = 0;
// primitive being clipped / primitive that produced the current best hit (for lastHitModel)
let curPrim = -1, hitPrim = -1;

/**
 * One plane of CM_TraceThroughBrush. Returns false when the move is completely in front of the plane
 * (the primitive cannot be hit), true otherwise. Same arithmetic as BoxWorld.clipBoxToAabb so axial
 * brushes give bit-identical results.
 */
function clipPlane(nx: number, ny: number, nz: number, d: number, surf: number): boolean {
  const dist = d - (nx * (nx < 0 ? MX0 : MN0) + ny * (ny < 0 ? MX1 : MN1) + nz * (nz < 0 ? MX2 : MN2));
  const d1 = (S0 * nx + S1 * ny + S2 * nz) - dist;
  const d2 = (E0 * nx + E1 * ny + E2 * nz) - dist;
  if (d2 > 0) getout = true;
  if (d1 > 0) startout = true;
  if (d1 > 0 && (d2 >= EPS || d2 >= d1)) return false;
  if (d1 <= 0 && d2 <= 0) return true;
  if (d1 > d2) {
    let f = (d1 - EPS) / (d1 - d2);
    if (f < 0) f = 0;
    if (f > enterFrac) { enterFrac = f; hitNx = nx; hitNy = ny; hitNz = nz; hitD = d; hitSurf = surf; }
  } else {
    let f = (d1 + EPS) / (d1 - d2);
    if (f > 1) f = 1;
    if (f < leaveFrac) leaveFrac = f;
  }
  return true;
}

function beginPrim(): void { enterFrac = -1; leaveFrac = 1; startout = false; getout = false; }

/**
 * Apply the per-primitive result to the trace (end of CM_TraceThroughBrush). `touch` = true accepts
 * enterFrac == leaveFrac: needed for triangles, whose face and back planes coincide when the hull has zero
 * thickness along the normal (point traces), which makes the enter and leave fractions exactly equal.
 */
function endPrim(out: TraceResult, contents: number, touch = false): void {
  if (!startout) {
    out.startsolid = true;
    if (!getout) { out.allsolid = true; out.fraction = 0; }
    out.contents = contents; // (Q3 sets contents only for allsolid; BoxWorld sets it on startsolid too)
    out.entityNum = ENTITYNUM_WORLD;
    return;
  }
  if ((enterFrac < leaveFrac || (touch && enterFrac === leaveFrac)) && enterFrac > -1 && enterFrac < out.fraction) {
    out.fraction = enterFrac < 0 ? 0 : enterFrac;
    out.normal[0] = hitNx; out.normal[1] = hitNy; out.normal[2] = hitNz;
    out.planeDist = hitD;
    out.contents = contents;
    out.surfaceFlags = hitSurf;
    out.surfaceType = surfaceTypeFromFlags(hitSurf);
    out.entityNum = ENTITYNUM_WORLD;
    hitPrim = curPrim;
  }
}

// ---- node slab test (segment vs node AABB grown by the hull + NODE_MARGIN) ----------------------------
let ZX = false, ZY = false, ZZ = false, IX = 0, IY = 0, IZ = 0;
let GX0 = 0, GY0 = 0, GZ0 = 0, GX1 = 0, GY1 = 0, GZ1 = 0;
function setupSlab(): void {
  const dx = E0 - S0, dy = E1 - S1, dz = E2 - S2;
  ZX = dx > -1e-12 && dx < 1e-12; ZY = dy > -1e-12 && dy < 1e-12; ZZ = dz > -1e-12 && dz < 1e-12;
  IX = ZX ? 0 : 1 / dx; IY = ZY ? 0 : 1 / dy; IZ = ZZ ? 0 : 1 / dz;
  GX0 = MX0 + NODE_MARGIN; GY0 = MX1 + NODE_MARGIN; GZ0 = MX2 + NODE_MARGIN; // lo = nodeMin - maxs - m
  GX1 = MN0 - NODE_MARGIN; GY1 = MN1 - NODE_MARGIN; GZ1 = MN2 - NODE_MARGIN; // hi = nodeMax - mins + m
}
/** Entry fraction in [0,1] of the segment into node n's grown box, or -1 if it misses. */
function slabTest(nb: Float64Array, n: number): number {
  const o = n * 6;
  let t0 = 0, t1 = 1;
  const lx = nb[o] - GX0, hx = nb[o + 3] - GX1;
  if (ZX) { if (S0 < lx || S0 > hx) return -1; } else {
    let a = (lx - S0) * IX, b = (hx - S0) * IX;
    if (a > b) { const t = a; a = b; b = t; }
    if (a > t0) t0 = a;
    if (b < t1) t1 = b;
    if (t0 > t1) return -1;
  }
  const ly = nb[o + 1] - GY0, hy = nb[o + 4] - GY1;
  if (ZY) { if (S1 < ly || S1 > hy) return -1; } else {
    let a = (ly - S1) * IY, b = (hy - S1) * IY;
    if (a > b) { const t = a; a = b; b = t; }
    if (a > t0) t0 = a;
    if (b < t1) t1 = b;
    if (t0 > t1) return -1;
  }
  const lz = nb[o + 2] - GZ0, hz = nb[o + 5] - GZ1;
  if (ZZ) { if (S2 < lz || S2 > hz) return -1; } else {
    let a = (lz - S2) * IZ, b = (hz - S2) * IZ;
    if (a > b) { const t = a; a = b; b = t; }
    if (a > t0) t0 = a;
    if (b < t1) t1 = b;
    if (t0 > t1) return -1;
  }
  return t0;
}

export class MeshCollisionWorld implements CollisionWorld {
  readonly name: string;
  readonly bounds: { mins: Vec3; maxs: Vec3 };
  readonly stats: MeshWorldStats;

  // brushes
  private readonly nBrushes: number;
  private readonly brushFirst: Int32Array;
  private readonly brushCount: Int32Array;
  private readonly planes: Float64Array;
  private readonly planeSurf: Uint32Array;
  // triangles
  private readonly tv: Float64Array; // 9 per tri
  private readonly tp: Float64Array; // face plane nx ny nz d
  private readonly triSurf: Uint32Array;
  // all primitives (brushes first, then triangles)
  private readonly primContents: Uint32Array;
  private readonly primBaseContents: Uint32Array;
  private readonly primModel: Int16Array;
  /**
   * Submodel (`*N`, 0 = world) of the primitive that produced the last trace's hit, or -1 (no hit). Lets the
   * sim tell which glass pane a missile hit. Not part of the CollisionWorld contract.
   */
  lastHitModel = -1;
  private readonly bvh: Bvh;
  private readonly nodeContents: Uint32Array;
  private readonly water: WaterSurface[] = [];
  private readonly stackN = new Int32Array(STACK_SIZE);
  private readonly stackT = new Float64Array(STACK_SIZE);
  private readonly tmpTrace: TraceResult;
  private readonly tmpEnd: Vec3 = [0, 0, 0];

  /**
   * @param bounds optional override for `bounds` (default: AABB of all player-solid primitives).
   */
  constructor(name: string, g: CollisionGeometryBuilder, bounds?: { mins: Vec3; maxs: Vec3 }) {
    this.name = name;
    this.nBrushes = g.numBrushes;
    const nTris = g.numTris;
    const nPrims = this.nBrushes + nTris;
    this.brushFirst = Int32Array.from(g.brushFirstPlane);
    this.brushCount = Int32Array.from(g.brushNumPlanes);
    this.planes = Float64Array.from(g.planes);
    this.planeSurf = Uint32Array.from(g.planeSurf);
    this.tv = Float64Array.from(g.triVerts);
    this.triSurf = Uint32Array.from(g.triSurf);
    this.tp = new Float64Array(nTris * 4);
    this.primContents = new Uint32Array(nPrims);
    const boxes = new Float64Array(nPrims * 6);
    for (let b = 0; b < this.nBrushes; b++) {
      this.primContents[b] = g.brushContents[b];
      for (let k = 0; k < 6; k++) boxes[b * 6 + k] = g.brushBounds[b * 6 + k];
    }
    const tv = this.tv;
    for (let t = 0; t < nTris; t++) {
      const o = t * 9;
      const e1x = tv[o + 3] - tv[o], e1y = tv[o + 4] - tv[o + 1], e1z = tv[o + 5] - tv[o + 2];
      const e2x = tv[o + 6] - tv[o], e2y = tv[o + 7] - tv[o + 1], e2z = tv[o + 8] - tv[o + 2];
      let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      nx /= len; ny /= len; nz /= len;
      this.tp[t * 4] = nx; this.tp[t * 4 + 1] = ny; this.tp[t * 4 + 2] = nz;
      this.tp[t * 4 + 3] = nx * tv[o] + ny * tv[o + 1] + nz * tv[o + 2];
      const p = this.nBrushes + t;
      this.primContents[p] = g.triContents[t];
      const q = p * 6;
      boxes[q] = Math.min(tv[o], tv[o + 3], tv[o + 6]);
      boxes[q + 1] = Math.min(tv[o + 1], tv[o + 4], tv[o + 7]);
      boxes[q + 2] = Math.min(tv[o + 2], tv[o + 5], tv[o + 8]);
      boxes[q + 3] = Math.max(tv[o], tv[o + 3], tv[o + 6]);
      boxes[q + 4] = Math.max(tv[o + 1], tv[o + 4], tv[o + 7]);
      boxes[q + 5] = Math.max(tv[o + 2], tv[o + 5], tv[o + 8]);
      if ((g.triContents[t] & CONTENTS.WATER) && (nz > 0.1 || nz < -0.1)) {
        this.water.push({ tri: t, mnx: boxes[q], mny: boxes[q + 1], mxx: boxes[q + 3], mxy: boxes[q + 4] });
      }
    }
    this.primBaseContents = this.primContents.slice();
    this.primModel = new Int16Array(nPrims);
    for (let b = 0; b < this.nBrushes; b++) this.primModel[b] = g.brushModel[b] ?? 0;
    for (let t = 0; t < nTris; t++) this.primModel[this.nBrushes + t] = g.triModel[t] ?? 0;
    this.bvh = buildBvh(boxes, nPrims);
    // contents OR per node, bottom-up (children always have larger indices than their parent)
    const nc = new Uint32Array(this.bvh.numNodes);
    const nd = this.bvh.nodeData;
    for (let i = this.bvh.numNodes - 1; i >= 0; i--) {
      const cnt = nd[i * 2 + 1];
      let c = 0;
      if (cnt > 0) {
        for (let k = nd[i * 2]; k < nd[i * 2] + cnt; k++) c |= this.primContents[this.bvh.primIndex[k]];
      } else if (nPrims > 0) {
        c = nc[nd[i * 2]] | nc[nd[i * 2] + 1];
      }
      nc[i] = c >>> 0;
    }
    this.nodeContents = nc;

    if (bounds) {
      this.bounds = { mins: [bounds.mins[0], bounds.mins[1], bounds.mins[2]], maxs: [bounds.maxs[0], bounds.maxs[1], bounds.maxs[2]] };
    } else {
      const mins: Vec3 = [Infinity, Infinity, Infinity], maxs: Vec3 = [-Infinity, -Infinity, -Infinity];
      const solid = CONTENTS.SOLID | CONTENTS.GLASS | CONTENTS.PLAYERCLIP;
      for (let p = 0; p < nPrims; p++) {
        if (!(this.primContents[p] & solid)) continue;
        for (let k = 0; k < 3; k++) {
          if (boxes[p * 6 + k] < mins[k]) mins[k] = boxes[p * 6 + k];
          if (boxes[p * 6 + 3 + k] > maxs[k]) maxs[k] = boxes[p * 6 + 3 + k];
        }
      }
      if (mins[0] > maxs[0]) { mins[0] = mins[1] = mins[2] = -4096; maxs[0] = maxs[1] = maxs[2] = 4096; }
      this.bounds = { mins, maxs };
    }
    this.stats = {
      brushes: this.nBrushes, brushPlanes: this.planes.length / 4, triangles: nTris,
      nodes: this.bvh.numNodes, depth: this.bvh.depth, waterSurfaces: this.water.length,
      bevelPlanes: g.bevelPlanes, axialPlanesAdded: g.axialPlanesAdded, rejectedBrushes: g.rejectedBrushes, rejectedTris: g.rejectedTris,
    };
    if (this.bvh.depth >= STACK_SIZE - 2) throw new Error(`collision BVH too deep (${this.bvh.depth})`);
    this.tmpTrace = {
      fraction: 1, endpos: [0, 0, 0], normal: [0, 0, 0], planeDist: 0, startsolid: false, allsolid: false,
      contents: 0, surfaceFlags: 0, surfaceType: 0, entityNum: ENTITYNUM_NONE,
    };
  }

  /**
   * Enable/disable every primitive of submodel `*model` (e.g. a shattered glass pane). Not part of the
   * CollisionWorld contract; the state must be driven identically on server and clients (it changes traces).
   * Returns the number of primitives affected.
   */
  setSubmodelEnabled(model: number, enabled: boolean): number {
    if (model === 0) return 0;
    let n = 0;
    for (let p = 0; p < this.primModel.length; p++) {
      if (this.primModel[p] !== model) continue;
      this.primContents[p] = enabled ? this.primBaseContents[p] : 0;
      n++;
    }
    return n;
  }

  trace(
    out: TraceResult, start: ReadonlyVec3, end: ReadonlyVec3, mins: ReadonlyVec3, maxs: ReadonlyVec3,
    contentMask: number,
  ): TraceResult {
    hitPrim = -1;
    out.fraction = 1; out.startsolid = false; out.allsolid = false;
    out.normal[0] = 0; out.normal[1] = 0; out.normal[2] = 0;
    out.planeDist = 0; out.contents = 0; out.surfaceFlags = 0; out.surfaceType = 0; out.entityNum = ENTITYNUM_NONE;
    S0 = start[0]; S1 = start[1]; S2 = start[2]; E0 = end[0]; E1 = end[1]; E2 = end[2];
    MN0 = mins[0]; MN1 = mins[1]; MN2 = mins[2]; MX0 = maxs[0]; MX1 = maxs[1]; MX2 = maxs[2];
    if (this.primContents.length > 0) this.traverse(out, contentMask);
    this.lastHitModel = hitPrim >= 0 && out.fraction < 1 ? this.primModel[hitPrim] : -1;
    if (out.allsolid) out.fraction = 0;
    const f = out.fraction;
    out.endpos[0] = start[0] + f * (end[0] - start[0]);
    out.endpos[1] = start[1] + f * (end[1] - start[1]);
    out.endpos[2] = start[2] + f * (end[2] - start[2]);
    return out;
  }

  /**
   * Reference trace without the BVH: clips every primitive in index order. Same results as trace() except
   * possibly which of several exactly-tied planes is reported. Tests/debug only (slow).
   */
  traceBruteForce(
    out: TraceResult, start: ReadonlyVec3, end: ReadonlyVec3, mins: ReadonlyVec3, maxs: ReadonlyVec3,
    contentMask: number,
  ): TraceResult {
    out.fraction = 1; out.startsolid = false; out.allsolid = false;
    out.normal[0] = 0; out.normal[1] = 0; out.normal[2] = 0;
    out.planeDist = 0; out.contents = 0; out.surfaceFlags = 0; out.surfaceType = 0; out.entityNum = ENTITYNUM_NONE;
    S0 = start[0]; S1 = start[1]; S2 = start[2]; E0 = end[0]; E1 = end[1]; E2 = end[2];
    MN0 = mins[0]; MN1 = mins[1]; MN2 = mins[2]; MX0 = maxs[0]; MX1 = maxs[1]; MX2 = maxs[2];
    for (let p = 0; p < this.primContents.length; p++) {
      const c = this.primContents[p];
      if (!(c & contentMask)) continue;
      curPrim = p;
      if (p < this.nBrushes) this.clipBrush(out, p, c); else this.clipTri(out, p - this.nBrushes, c);
      if (out.allsolid) break;
    }
    if (out.allsolid) out.fraction = 0;
    const f = out.fraction;
    out.endpos[0] = start[0] + f * (end[0] - start[0]);
    out.endpos[1] = start[1] + f * (end[1] - start[1]);
    out.endpos[2] = start[2] + f * (end[2] - start[2]);
    return out;
  }

  private traverse(out: TraceResult, mask: number): void {
    const nb = this.bvh.nodeBounds, nd = this.bvh.nodeData, pi = this.bvh.primIndex, nc = this.nodeContents;
    const stackN = this.stackN, stackT = this.stackT;
    setupSlab();
    const slab = slabTest;
    if (!(nc[0] & mask)) return;
    const t = slab(nb, 0);
    if (t < 0) return;
    let sp = 0;
    stackN[sp] = 0; stackT[sp] = t; sp++;
    while (sp > 0) {
      sp--;
      // prune: everything in this node enters later than the best hit so far (strictly)
      if (stackT[sp] > out.fraction) continue;
      const node = stackN[sp];
      const cnt = nd[node * 2 + 1];
      if (cnt > 0) {
        const first = nd[node * 2];
        for (let k = first; k < first + cnt; k++) {
          const p = pi[k];
          const c = this.primContents[p];
          if (!(c & mask)) continue;
          curPrim = p;
          if (p < this.nBrushes) this.clipBrush(out, p, c); else this.clipTri(out, p - this.nBrushes, c);
          if (out.allsolid) return;
        }
        continue;
      }
      const l = nd[node * 2], r = l + 1;
      const tl = nc[l] & mask ? slab(nb, l) : -1;
      const tr = nc[r] & mask ? slab(nb, r) : -1;
      // push the farther child first so the nearer one is visited first
      if (tl >= 0 && tr >= 0) {
        if (tl <= tr) { stackN[sp] = r; stackT[sp] = tr; sp++; stackN[sp] = l; stackT[sp] = tl; sp++; }
        else { stackN[sp] = l; stackT[sp] = tl; sp++; stackN[sp] = r; stackT[sp] = tr; sp++; }
      } else if (tl >= 0) { stackN[sp] = l; stackT[sp] = tl; sp++; }
      else if (tr >= 0) { stackN[sp] = r; stackT[sp] = tr; sp++; }
    }
  }

  private clipBrush(out: TraceResult, b: number, contents: number): void {
    const P = this.planes, PS = this.planeSurf;
    beginPrim();
    const first = this.brushFirst[b], end = first + this.brushCount[b];
    for (let i = first; i < end; i++) {
      const o = i * 4;
      if (!clipPlane(P[o], P[o + 1], P[o + 2], P[o + 3], PS[i])) return;
    }
    endPrim(out, contents);
  }

  /**
   * Triangle as a zero-thickness brush: 6 axial planes of its AABB, the face plane, the back face, and the
   * edge x axis bevels that support it (separating axes of box vs triangle). Generated on the fly.
   */
  private clipTri(out: TraceResult, t: number, contents: number): void {
    const v = this.tv, o = t * 9;
    const ax = v[o], ay = v[o + 1], az = v[o + 2], bx = v[o + 3], by = v[o + 4], bz = v[o + 5];
    const cx = v[o + 6], cy = v[o + 7], cz = v[o + 8];
    const surf = this.triSurf[t];
    beginPrim();
    // axial (same order as brushes: -x +x -y +y -z +z)
    if (!clipPlane(-1, 0, 0, -Math.min(ax, bx, cx), surf)) return;
    if (!clipPlane(1, 0, 0, Math.max(ax, bx, cx), surf)) return;
    if (!clipPlane(0, -1, 0, -Math.min(ay, by, cy), surf)) return;
    if (!clipPlane(0, 1, 0, Math.max(ay, by, cy), surf)) return;
    if (!clipPlane(0, 0, -1, -Math.min(az, bz, cz), surf)) return;
    if (!clipPlane(0, 0, 1, Math.max(az, bz, cz), surf)) return;
    const q = t * 4;
    const nx = this.tp[q], ny = this.tp[q + 1], nz = this.tp[q + 2], nd = this.tp[q + 3];
    if (!clipPlane(nx, ny, nz, nd, surf)) return;
    if (!clipPlane(-nx, -ny, -nz, -nd, surf)) return;
    // edge bevels
    for (let e = 0; e < 3; e++) {
      let px: number, py: number, pz: number, qx: number, qy: number, qz: number, rx: number, ry: number, rz: number;
      if (e === 0) { px = ax; py = ay; pz = az; qx = bx; qy = by; qz = bz; rx = cx; ry = cy; rz = cz; }
      else if (e === 1) { px = bx; py = by; pz = bz; qx = cx; qy = cy; qz = cz; rx = ax; ry = ay; rz = az; }
      else { px = cx; py = cy; pz = cz; qx = ax; qy = ay; qz = az; rx = bx; ry = by; rz = bz; }
      const ex = qx - px, ey = qy - py, ez = qz - pz;
      for (let axis = 0; axis < 3; axis++) {
        let mx: number, my: number, mz: number;
        if (axis === 0) { mx = 0; my = ez; mz = -ey; } else if (axis === 1) { mx = -ez; my = 0; mz = ex; } else { mx = ey; my = -ex; mz = 0; }
        const len2 = mx * mx + my * my + mz * mz;
        if (len2 < 1e-12) continue;
        const inv = 1 / Math.sqrt(len2);
        mx *= inv; my *= inv; mz *= inv;
        const dn = mx * nx + my * ny + mz * nz;
        if (dn > 0.99999 || dn < -0.99999) continue; // parallel to the face: face/back planes cover it
        let d = mx * px + my * py + mz * pz;
        const dr = mx * rx + my * ry + mz * rz;
        if (dr > d) { mx = -mx; my = -my; mz = -mz; d = -d; } // third vertex must be behind
        if (!clipPlane(mx, my, mz, d, surf)) return;
      }
    }
    endPrim(out, contents, true);
  }

  pointContents(p: ReadonlyVec3, contentMask: number): number {
    let c = 0;
    if (this.primContents.length > 0 && (this.nodeContents[0] & contentMask)) {
      const nb = this.bvh.nodeBounds, nd = this.bvh.nodeData, pi = this.bvh.primIndex, nc = this.nodeContents;
      const P = this.planes;
      const x = p[0], y = p[1], z = p[2];
      const stack = this.stackN;
      let sp = 0;
      stack[sp++] = 0;
      while (sp > 0) {
        const node = stack[--sp];
        const o = node * 6;
        if (!(nc[node] & contentMask)) continue;
        if (x < nb[o] || x > nb[o + 3] || y < nb[o + 1] || y > nb[o + 4] || z < nb[o + 2] || z > nb[o + 5]) continue;
        const cnt = nd[node * 2 + 1];
        if (cnt === 0) { stack[sp++] = nd[node * 2] + 1; stack[sp++] = nd[node * 2]; continue; }
        for (let k = nd[node * 2]; k < nd[node * 2] + cnt; k++) {
          const b = pi[k];
          if (b >= this.nBrushes) continue; // triangles enclose no volume (Q3 CM_PointContents: brushes only)
          const bc = this.primContents[b];
          if (!(bc & contentMask)) continue;
          let inside = true;
          for (let i = this.brushFirst[b], e = i + this.brushCount[b]; i < e; i++) {
            if (P[i * 4] * x + P[i * 4 + 1] * y + P[i * 4 + 2] * z - P[i * 4 + 3] > 0) { inside = false; break; }
          }
          if (inside) c |= bc;
        }
      }
    }
    if ((contentMask & CONTENTS.WATER) && !(c & CONTENTS.WATER) && this.inWater(p)) c |= CONTENTS.WATER;
    return (c & contentMask) >>> 0;
  }

  /**
   * Water is exported as surfaces (Hijacked: the ocean at z = -315 and the yacht's pools/hot tub), not as
   * volumes. ESTIMATE: a point is in water when a water surface lies above it in its column and nothing
   * SOLID is between the point and that surface (so the pool floor bounds the pool's water).
   */
  private inWater(p: ReadonlyVec3): boolean {
    const x = p[0], y = p[1], z = p[2];
    for (const w of this.water) {
      if (x < w.mnx || x > w.mxx || y < w.mny || y > w.mxy) continue;
      const o = w.tri * 9, v = this.tv;
      // 2D point-in-triangle (xy projection), inclusive
      const d1 = (x - v[o + 3]) * (v[o + 1] - v[o + 4]) - (v[o] - v[o + 3]) * (y - v[o + 4]);
      const d2 = (x - v[o + 6]) * (v[o + 4] - v[o + 7]) - (v[o + 3] - v[o + 6]) * (y - v[o + 7]);
      const d3 = (x - v[o]) * (v[o + 7] - v[o + 1]) - (v[o + 6] - v[o]) * (y - v[o + 1]);
      const neg = d1 < 0 || d2 < 0 || d3 < 0, pos = d1 > 0 || d2 > 0 || d3 > 0;
      if (neg && pos) continue;
      const q = w.tri * 4;
      const zs = (this.tp[q + 3] - this.tp[q] * x - this.tp[q + 1] * y) / this.tp[q + 2];
      if (zs < z) continue;
      const tr = this.tmpTrace;
      const top = this.tmpEnd;
      top[0] = x; top[1] = y; top[2] = zs;
      this.trace(tr, p, top, ZERO3, ZERO3, CONTENTS.SOLID);
      if (tr.fraction === 1) return true;
    }
    return false;
  }
}
